import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Grid,
  Paper,
  Typography,
  IconButton,
  Chip,
  Modal,
} from "@mui/material";
import { Add as AddIcon,Edit as EditIcon } from "@mui/icons-material";
import axios from "../../api";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import { useParams, useLocation } from "react-router-dom";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useCustomerId } from "context/CustomerIdContext";
import SoftBox from "components/SoftBox";
import EditAddress from "./EditAddress";
import AddAddress from "./AddAddress";
import SlideOverPanel from "components/SlideOverPanel";
import { AxiosError } from "axios";

interface Address {
  state_id: number;
  city_id: number;
  region_id: number;
  address_id: number;
  customer_id: number;
  is_primary: boolean;
  state_name: string;
  city_name: string;
  region_name: string;
  address_type: string;
  address: string;
  division_name: string;
}

interface ViewAddressesProps {
  /** When used in overlay, pass customer type for Add Address form */
  customerType?: string;
  /** When provided, "Add Address" closes current overlay and parent opens Add Address overlay (single slide) */
  onOpenAddAddress?: (customerId: number, customerType: string) => void;
}

const ViewAddresses = ({ customerType: propCustomerType, onOpenAddAddress }: ViewAddressesProps = {}) => {
  const location = useLocation();
  const { type: locationType } = location.state || {};
  const type = propCustomerType ?? locationType ?? "customer";
  const { fetchError, fetchSuccess } = useSnackbar();
  const contextId = useCustomerId();
  const { id: paramId } = useParams();
  const id = contextId ?? paramId;

  const [addressData, setAddressData] = useState<Address[] | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [addAddressOpen, setAddAddressOpen] = useState(false);

  const handlePrimaryAddress = async (
    event: React.ChangeEvent<HTMLInputElement>,
    addressId: number,
    userId: number
  ) => {
    await axios.put(`/address/is-primary/${addressId}`, {
      is_primary: event.target.checked,
      customer_id: userId,
    });

    fetchAddressData();
    fetchSuccess("Address selected as primary");
  };

  const fetchAddressData = useCallback(async () => {
    try {
      const response = await axios.get(`/address/customer/${id}`);
      setAddressData(response?.data?.list);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchAddressData();
  }, [fetchAddressData]);

  const handleEditOpen = (data: Address) => {
    setAddress(data);
    setIsEditOpen(true);
  };

  const handleEditClose = () => setIsEditOpen(false);

  if (!addressData) return <PageLoader />;

  return (
    <Card sx={{ p: 3, maxWidth: "100%", background: "#fff" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
          Manage Addresses
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            if (onOpenAddAddress && id) {
              onOpenAddAddress(Number(id), type);
            } else {
              setAddAddressOpen(true);
            }
          }}
          size="small"
        >
          Add Address
        </Button>
      </Box>
      <SoftBox mt={2}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={12}>
            {addressData.map((data) => (
              <Paper
                key={data.address_id}
                elevation={0}
                sx={{
                  p: 1,
                  mb: 3,
                  borderRadius: "12px",
                  border: data.is_primary
                    ? "1px solid #8CD6BD"
                    : "1px solid #E0E0E0",
                  backgroundColor: data.is_primary ? "#F3FFFB" : "#fff",
                }}
              >
                {/* TOP HEADER */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {/* Left – Primary / Secondary Chip */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        px: 1.4,
                        py: 0.4,
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        display: "inline-block",
                        color: data.is_primary ? "#008080" : "#000",
                      }}
                    >
                      {data.is_primary ? "Primary" : "Secondary"}
                    </Box>

                    <Chip
                      label={data.address_type}
                      size="small"
                      sx={{
                        backgroundColor: "#ECECEC",
                        fontSize: "12px",
                        height: "22px",
                      }}
                    />
                  </Box>

                  {/* Right – Edit icon */}
                  <Box display="flex" alignItems="center">
                    <Checkbox
                      checked={data.is_primary}
                      onChange={(event) =>
                        handlePrimaryAddress(
                          event,
                          data.address_id,
                          data.customer_id
                        )
                      }
                    />
                    <IconButton onClick={() => handleEditOpen(data)}>
                      <EditIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* DETAILS */}
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} sm={4}>
                    <TextRow label="State" value={data.state_name} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextRow label="City" value={data.city_name} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextRow label="Region" value={data.region_name} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextRow label="Address" value={data.address} />
                  </Grid>

                  {type === "company" && (
                    <Grid item xs={12} sm={4}>
                      <TextRow label="Division" value={data.division_name} />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))}
          </Grid>
        </Grid>
      </SoftBox>

      {/* Edit Modal */}
      <Modal open={isEditOpen} onClose={handleEditClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            p: 4,
            borderRadius: 2,
            width: 500,
            maxHeight: "85vh",
            overflowY: "auto",
          }}
        >
          <EditAddress
            data={address}
            fetchSuccess={fetchSuccess}
            fetchError={fetchError}
            fetchAddressData={fetchAddressData}
            onClose={handleEditClose}
          />
        </Box>
      </Modal>

      {/* Add Address slide overlay (only when parent doesn't handle it via onOpenAddAddress) */}
      {!onOpenAddAddress && (
        <SlideOverPanel
          open={addAddressOpen}
          onClose={() => setAddAddressOpen(false)}
          title="Add Address"
          width={700}
          containerZIndex={1300}
        >
          {id && (
            <AddAddress
              onClose={() => setAddAddressOpen(false)}
              customerId={String(id)}
              customerType={type}
              fetchData={fetchAddressData}
              fetchSuccess={fetchSuccess}
              fetchError={fetchError}
            />
          )}
        </SlideOverPanel>
      )}
    </Card>
  );
};

const TextRow = ({ label, value }: any) => (
  <Box>
    <Typography fontSize={14} fontWeight={600} color="#555">
      {label}
    </Typography>
    <Typography fontSize={15} mt={0.3} color="#222">
      {value}
    </Typography>
  </Box>
);

export default ViewAddresses;
